/**
 * Image utilities for handling Cloudinary URLs and optimizations
 */

export interface ResponsiveImages {
  thumbnail: string;
  small: string;
  medium: string;
  large: string;
  original: string;
}

/**
 * Check if image URL is from Cloudinary
 */
export const isCloudinaryUrl = (url: string): boolean => {
  return url?.includes('cloudinary.com') || false;
};

/**
 * Get Cloudinary transformation URL
 */
export const getCloudinaryUrl = (
  url: string, 
  transformations: Record<string, any> = {}
): string => {
  if (!url || !isCloudinaryUrl(url)) return url;
  
  try {
    // Parse existing transformations from URL
    const urlParts = url.split('/upload/');
    if (urlParts.length !== 2) return url;
    
    const [baseUrl, pathPart] = urlParts;
    
    // Build transformation string
    const transformParams = [];
    
    if (transformations.width) transformParams.push(`w_${transformations.width}`);
    if (transformations.height) transformParams.push(`h_${transformations.height}`);
    if (transformations.crop) transformParams.push(`c_${transformations.crop}`);
    if (transformations.quality) transformParams.push(`q_${transformations.quality}`);
    if (transformations.format) transformParams.push(`f_${transformations.format}`);
    
    // Always add auto quality and format for optimization
    if (!transformations.quality) transformParams.push('q_auto');
    if (!transformations.format) transformParams.push('f_auto');
    
    const transformString = transformParams.join(',');
    
    // Remove existing transformations from path
    const cleanPath = pathPart.replace(/^v\d+\//, '').replace(/^[^\/]*\//, '');
    
    return `${baseUrl}/upload/${transformString}/v1/${cleanPath}`;
  } catch (error) {
    console.error('Error generating Cloudinary URL:', error);
    return url;
  }
};

/**
 * Get responsive image URLs
 */
export const getResponsiveImages = (originalUrl: string): ResponsiveImages => {
  if (!originalUrl) {
    const fallback = '/placeholder-image.jpg';
    return {
      thumbnail: fallback,
      small: fallback,
      medium: fallback,
      large: fallback,
      original: fallback
    };
  }

  if (!isCloudinaryUrl(originalUrl)) {
    return {
      thumbnail: originalUrl,
      small: originalUrl,
      medium: originalUrl,
      large: originalUrl,
      original: originalUrl
    };
  }

  return {
    thumbnail: getCloudinaryUrl(originalUrl, { width: 200, height: 150, crop: 'fill' }),
    small: getCloudinaryUrl(originalUrl, { width: 400, height: 300, crop: 'fill' }),
    medium: getCloudinaryUrl(originalUrl, { width: 600, height: 450, crop: 'fill' }),
    large: getCloudinaryUrl(originalUrl, { width: 1000, height: 750, crop: 'fill' }),
    original: originalUrl
  };
};

/**
 * Get optimized banner image
 */
export const getBannerImage = (
  originalUrl: string, 
  width: number = 1200, 
  height: number = 400
): string => {
  if (!originalUrl) return '/placeholder-banner.jpg';
  
  if (!isCloudinaryUrl(originalUrl)) return originalUrl;
  
  return getCloudinaryUrl(originalUrl, {
    width,
    height,
    crop: 'fill',
    gravity: 'center'
  });
};

/**
 * Get square thumbnail for gallery previews
 */
export const getThumbnail = (originalUrl: string, size: number = 200): string => {
  if (!originalUrl) return '/placeholder-thumbnail.jpg';
  
  if (!isCloudinaryUrl(originalUrl)) return originalUrl;
  
  return getCloudinaryUrl(originalUrl, {
    width: size,
    height: size,
    crop: 'fill',
    gravity: 'center'
  });
};

/**
 * Check if image upload is supported
 */
export const isImageUploadSupported = (): boolean => {
  return 'FileReader' in window && 'File' in window && 'FormData' in window;
};

/**
 * Validate image file
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Please upload a JPEG, PNG, or WebP image' };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'Image must be less than 10MB' };
  }
  
  return { valid: true };
};

/**
 * Validate highlight image file (smaller size limit)
 */
export const validateHighlightImage = (file: File): { valid: boolean; error?: string } => {
  const maxSize = 2 * 1024 * 1024; // 2MB for highlights
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Please upload a JPEG, PNG, or WebP image' };
  }
  
  if (file.size > maxSize) {
    const sizeInMB = (file.size / 1024 / 1024).toFixed(1);
    return { valid: false, error: `Highlight image must be less than 2MB (current: ${sizeInMB}MB)` };
  }
  
  return { valid: true };
};

/**
 * Get optimized Cloudinary URL for highlights
 */
export const getHighlightImageUrl = (url: string): string => {
  if (!url) return '/api/placeholder/400/300';
  
  return getCloudinaryUrl(url, {
    width: 600,
    height: 400,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  });
};

/**
 * Get thumbnail version of Cloudinary image for highlights
 */
export const getHighlightThumbnail = (url: string): string => {
  if (!url) return '/api/placeholder/200/150';
  
  return getCloudinaryUrl(url, {
    width: 200,
    height: 150,
    crop: 'fill',
    quality: '60',
    format: 'auto'
  });
};
